import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Card, Col, Form, Row } from 'antd';
import { PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import configurationService from 'common/services/configuration-service/configuration.service';
import configurationStore from 'common/stores/configuration/configuration.store';
import featureStore from 'common/stores/features/feature.store';
import { notification } from 'common/utils/notification';
import CitationGeneralSettings from './citation-general-settings';
import CitationRuleConfiguration from './citation-rule-configuration';
import { ICitationConfigurationFormValues } from './citation-configuration-form.types';
import { createEmptyRule, mapConfigurationToFormValues, mapFormValuesToConfigurationUpdate } from './citation-configuration.helper';
import './citations-configurations.scss';

const CitationConfigurations = observer(() => {
  const [form] = Form.useForm<ICitationConfigurationFormValues>();
  const configuration = configurationStore.configuration?.citations;
  const isEscalationTiersEnabled = featureStore.isEIQIntelliTicketEscalationTiersEnabled;

  useEffect(() => {
    if (!configuration) {
      return;
    }

    form.setFieldsValue(mapConfigurationToFormValues(configuration, isEscalationTiersEnabled));
  }, [configuration, form, isEscalationTiersEnabled]);

  const handleSubmit = async (values: ICitationConfigurationFormValues) => {
    const update = mapFormValuesToConfigurationUpdate(values, isEscalationTiersEnabled);
    const result = await configurationService.updateSystemConfiguration(update);

    configurationStore.setConfiguration(result);
    notification.success({
      message: 'Configurations successfully updated',
      duration: 5,
    });
  };

  return (
    <FormWrapper form={form} onFinish={handleSubmit} layout="vertical">
      <Card className="eiq-card eiq-card-border eiq-configuration">
        <Row className="eiq-card-header">
          <Col span={24}>
            <h1 className="h1">IntelliTicket Settings</h1>
          </Col>
        </Row>

        <CitationGeneralSettings form={form} showLegacyToleranceFields={!isEscalationTiersEnabled} />

        <Row>
          <Col xs={24}>
            <div className="citation-levels-title">Citation Levels &amp; Penalties</div>

            <Form.List name="rules">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <CitationRuleConfiguration key={key} index={index} name={name} restField={restField} remove={remove} form={form} />
                  ))}

                  <Form.Item>
                    <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add(createEmptyRule(fields.length))}>
                      Add rule
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <div className="save-citation-configuration-button">
              <Button htmlType="submit" type="primary" icon={<SaveOutlined />} block>
                Save
              </Button>
            </div>
          </Col>
        </Row>
      </Card>
    </FormWrapper>
  );
});

export default CitationConfigurations;
